import dayjs from 'dayjs'
import { isString, isNumber } from 'lodash'
import { IErrorResult } from './type'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { branchConfigTimeConverter } from '../times'

dayjs.extend(customParseFormat)

const ERROR_MESSAGE = {
  // eslint-disable-next-line quotes
  isSameDate: "This Date isn't the current date.",
  isAfterDate: 'ra.validation.invalid_after_date',
  isBeforeDate: 'ra.validation.invalid_before_date',
  isAfterTime: 'ra.validation.invalid_after_time',
  isBeforeTime: 'ra.validation.invalid_before_time',
  isSameData: 'ra.validation.data_not_match',
  isLatitude: 'ra.validation.invalid_latitude',
  isLongitude: 'ra.validation.invalid_longitude',
  isPhoneNumber: 'ra.validation.invalid_phone_number',
  isFaxNumber: 'Data do not fax number.',
  isOrderNumber: 'Data is not order number.',
  emailPattern: 'ra.validation.invalid_email',
  passwordPattern: 'ra.validation.password_pattern',
  isYoutubeLink:
    'Only Youtube link format example: https://www.youtube.com/, https://m.youtube.com/ or https://youtu.be/',
  branchConfigOpenSlot: 'ra.validation.open_slot_greater_than_slot',
  openTimeInvalid: 'เวลาเปิดต้องน้อยกว่าเวลาปิด',
  closeTimeInvalid: 'เวลาปิดต้องมากกว่าเวลาเปิด',
}

export const emailPattern =
  (message = ERROR_MESSAGE.emailPattern) =>
  (value): IErrorResult => {
    const pattern = /^[a-z\d@._\w-]+$/i
    return !pattern.test(value) ? message : undefined
  }

export const passwordPattern =
  (message = ERROR_MESSAGE.passwordPattern) =>
  (value): IErrorResult => {
    const pattern = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/
    return !pattern.test(value) ? message : undefined
  }

export const isSameDate =
  (
    dateCompared: dayjs.Dayjs | Date = new Date(),
    unit: dayjs.OpUnitType = 'date',
    message = ERROR_MESSAGE.isSameDate,
  ) =>
  (value): IErrorResult => {
    return dayjs(new Date(value)).isSame(dateCompared, unit) ? message : undefined
  }

export const isAfterDate =
  (sourceDateCompared, message = ERROR_MESSAGE.isAfterDate) =>
  (value, values): IErrorResult =>
    !dayjs(new Date(value)).isAfter(new Date(values[sourceDateCompared])) ? message : undefined

export const isBeforeDate =
  (sourceDateCompared, message = ERROR_MESSAGE.isBeforeDate) =>
  (value, values): IErrorResult =>
    !dayjs(new Date(value)).isBefore(new Date(values[sourceDateCompared])) ? message : undefined

export const isAfterTime =
  (sourceDateCompared, message = ERROR_MESSAGE.isAfterTime) =>
  (value, values): IErrorResult =>
    !dayjs(value, 'HH:mm').isAfter(dayjs(values[sourceDateCompared], 'HH:mm')) ? message : undefined

export const isBeforeTime =
  (sourceDateCompared, message = ERROR_MESSAGE.isBeforeTime) =>
  (value, values): IErrorResult =>
    !dayjs(value, 'HH:mm').isBefore(dayjs(values[sourceDateCompared], 'HH:mm'))
      ? message
      : undefined

export const isSameData =
  (sourceDateCompared, message = ERROR_MESSAGE.isSameData) =>
  (value, values): IErrorResult =>
    value != values[sourceDateCompared] ? message : undefined

export const isLatitude =
  (canEmpty = false, message = ERROR_MESSAGE.isLatitude) =>
  (value): IErrorResult => {
    const pattern = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/
    if (canEmpty && !value) return undefined
    return !pattern.test(value) ? message : undefined
  }

export const isLongitude =
  (canEmpty = false, message = ERROR_MESSAGE.isLongitude) =>
  (value): IErrorResult => {
    const pattern = /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/
    if (canEmpty && !value) return undefined
    return !pattern.test(value) ? message : undefined
  }

export const isPhoneNumber =
  (canEmpty = false, message = ERROR_MESSAGE.isPhoneNumber) =>
  (value): IErrorResult => {
    const pattern = /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/
    if (canEmpty && !value) return undefined
    return !pattern.test(value) ? message : undefined
  }

export const isFaxNumber =
  (canEmpty = false, message = ERROR_MESSAGE.isFaxNumber) =>
  (value): IErrorResult => {
    const pattern = /^([0-9]{6,10})$/
    if (canEmpty && !value) return undefined
    return !pattern.test(value) ? message : undefined
  }

export const isOrderNumber =
  (canEmpty = false, message = ERROR_MESSAGE.isOrderNumber) =>
  (value): IErrorResult => {
    const pattern = /^[+]?[1-9]\d*$/
    if (canEmpty && !value) return undefined
    return !pattern.test(value) ? message : undefined
  }

export const isBlob = (value): boolean => isString(value) && value.startsWith('blob:')

export const isYoutubeLink =
  (canEmpty = true, message = ERROR_MESSAGE.isYoutubeLink) =>
  (value): IErrorResult => {
    const pattern = /^(https?:\/\/)?(m\.|www\.)?(youtube\.com|youtu\.be)\/.*$/
    if (canEmpty && !value) return undefined
    return !pattern.test(value) ? message : undefined
  }

export const openTime = (
  value: string,
  { open_time: openTime, close_time: closeTime },
): IErrorResult => {
  const o = isNumber(openTime) ? branchConfigTimeConverter(openTime).format('HH:mm') : openTime
  const c = isNumber(closeTime) ? branchConfigTimeConverter(closeTime).format('HH:mm') : closeTime
  return !dayjs(o, 'HH:mm').isBefore(dayjs(c, 'HH:mm')) ? ERROR_MESSAGE.openTimeInvalid : undefined
}

export const closeTime = (
  value: string,
  { open_time: openTime, close_time: closeTime },
): IErrorResult => {
  const o = isNumber(openTime) ? branchConfigTimeConverter(openTime).format('HH:mm') : openTime
  const c = isNumber(closeTime) ? branchConfigTimeConverter(closeTime).format('HH:mm') : closeTime
  return !dayjs(c, 'HH:mm').isAfter(dayjs(o, 'HH:mm')) ? ERROR_MESSAGE.closeTimeInvalid : undefined
}

export const validateOpenSlot = (value, allValues): IErrorResult => {
  const slotValue = allValues.slot
  if (value > slotValue) {
    return ERROR_MESSAGE.branchConfigOpenSlot
  }
  return undefined
}
